/**
 * Toast notification utilities using Sonner
 *
 * This module provides a consistent way to show toast notifications
 * across the application. It wraps Sonner's toast function with
 * predefined configurations for different notification types.
 *
 * @example
 * ```tsx
 * import { toast } from '@/utils/toast';
 *
 * // Error notification
 * toast.error('Failed to save changes');
 *
 * // Success notification
 * toast.success('Profile updated successfully');
 *
 * // Warning notification
 * toast.warning('Your session is about to expire');
 *
 * // Info notification
 * toast.info('New features available');
 * ```
 */

import { toast as sonnerToast } from "sonner";

type ToastAction = {
  label: string;
  onClick: () => void;
};

type ToastOptions = {
  description?: string;
  duration?: number;
  action?: ToastAction;
};

/**
 * Default duration for toast notifications in milliseconds
 */
const DEFAULT_DURATION = 4000;

/**
 * Extended duration for important notifications
 */
const EXTENDED_DURATION = 6000;

/**
 * Toast notification utilities
 */
export const toast = {
  /**
   * Show a success toast notification
   * @param message - The message to display
   * @param options - Additional options (e.g., description, duration)
   */
  success: (
    message: string,
    options?: ToastOptions,
  ) => {
    sonnerToast.success(message, {
      duration: options?.duration ?? DEFAULT_DURATION,
      description: options?.description,
      action: options?.action,
    });
  },

  /**
   * Show an error toast notification
   * @param message - The error message to display
   * @param options - Additional options (e.g., description, duration)
   */
  error: (
    message: string,
    options?: ToastOptions,
  ) => {
    sonnerToast.error(message, {
      duration: options?.duration ?? EXTENDED_DURATION,
      description: options?.description,
    });
  },

  /**
   * Show a warning toast notification
   * @param message - The warning message to display
   * @param options - Additional options (e.g., description, duration)
   */
  warning: (
    message: string,
    options?: ToastOptions,
  ) => {
    sonnerToast.warning(message, {
      duration: options?.duration ?? EXTENDED_DURATION,
      description: options?.description,
      action: options?.action,
    });
  },

  /**
   * Show an info toast notification
   * @param message - The info message to display
   * @param options - Additional options (e.g., description, duration)
   */
  info: (
    message: string,
    options?: ToastOptions,
  ) => {
    sonnerToast.info(message, {
      duration: options?.duration ?? DEFAULT_DURATION,
      description: options?.description,
    });
  },

  /**
   * Show a loading toast that can be updated
   * @param message - The loading message to display
   * @returns A function to dismiss or update the toast
   */
  loading: (message: string) => {
    return sonnerToast.loading(message);
  },

  /**
   * Dismiss a specific toast or all toasts
   * @param toastId - Optional toast ID to dismiss a specific toast
   */
  dismiss: (toastId?: string | number) => {
    sonnerToast.dismiss(toastId);
  },

  /**
   * Update an existing toast (useful for loading → success/error transitions)
   * @param toastId - The toast ID to update
   * @param message - The new message
   * @param type - The new toast type
   */
  update: (
    toastId: string | number,
    message: string,
    type: "success" | "error" | "info" | "warning",
  ) => {
    sonnerToast.dismiss(toastId);
    toast[type](message);
  },
};
